/*
 *
 *  * Copyright(c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Rect } from '../types/rect';

@Injectable({
  providedIn: 'root'
})
export class DOMService {
  private window: Window;

  constructor(@Inject(DOCUMENT) private document: any) {
    this.window = (this.document as Document).defaultView;
  }

  getRect(element: HTMLElement): Rect {
    if (!element) return { top: 0, left: 0, height: 0, width: 0 };

    const rect = element.getBoundingClientRect();
    return {
      top: rect.top + this.window.pageYOffset,
      left: rect.left + this.window.pageXOffset,
      height: rect.height,
      width: rect.width
    };
  }

  /**
   * Returns a bitwise number where each bit represents a position
   * top | bottom | left | right
   *
   * ex: 0101 => overflow on bottom and right positions (number 5)
   */
  identifyWindowOverflow(element: HTMLElement): number {
    const rect = this.getRect(element);
    let overflow = 0;

    overflow += (rect.top < 0 && 8) || 0;
    overflow += (rect.top + rect.height > this.window.innerHeight && 4) || 0;
    overflow += (rect.left < 0 && 2) || 0;
    overflow += (rect.left + rect.width > this.window.innerWidth && 1) || 0;

    return overflow;
  }

  getEffectiveZIndex(element: HTMLElement): number {
    if (!element) return 0;

    const zIndex = +this.window.getComputedStyle(element).getPropertyValue('z-index') || 0;

    return zIndex + this.getEffectiveZIndex(element.parentElement);
  }
}
